<template>
  <div>
    <v-row>
      <v-col cols="6"><h1 class="font-weight-thin">Tag Administration</h1></v-col>
    </v-row>
    <v-row>
      <v-col cols="10">
        <div>
          This admin module lets you create tags to apply to release notes to help with organization filtering.
        </div>
      </v-col>
    </v-row>
    <v-app-bar class="mt-3" flat color="gray" dense>
      <v-toolbar-title class="text-h6 pl-0 font-weight-thin">
        Tag List
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn @click="showCreateForm()" small outlined color="primary" data-cy="createButton">
        <v-icon class="mr-2" small>fas fa-plus</v-icon>Add
      </v-btn>
    </v-app-bar>

    <v-divider class="mt-5"></v-divider>
    <v-col>
      <generic-loader caption="Fetching data..." topMargin="10" v-if="isLoadingTags"></generic-loader>
      <v-card class="elevation-0 mt-5" v-else>
        <v-data-table
          :headers="headers"
          hide-default-footer
          :items="tagList"
          :search="search"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          data-cy="memberTagTable"
          item-key="id"
          disable-pagination
        >
          <template v-slot:top>
            <v-row>
              <v-col sm="12" md="6">
                <v-text-field
                  clearable
                  dense
                  label="Search"
                  outlined
                  prepend-inner-icon="search"
                  single-line
                  v-model="search"
                ></v-text-field>
              </v-col>
            </v-row>
          </template>
          <template v-slot:item="{ item }">
            <tr>
              <td data-cy="label" width="250rem">{{ item.label }}</td>
              <td data-cy="description" width="500rem">{{ item.description }}</td>
              <td data-cy="usage">
                <v-btn
                  v-if="item.releaseNotesUsingTag > 0"
                  @click="showReleaseNoteUsageModal(item)"
                  color="primary"
                  data-cy="testPlanListDialog"
                  text
                  small
                  >Release Notes: {{ item.releaseNotesUsingTag }}</v-btn
                >
                <release-note-tag-usage-dialog v-model="showUsage" :tag="item" :releaseNoteList="releaseNoteList">
                </release-note-tag-usage-dialog>
              </td>
              <td>
                <v-btn @click="showEditForm(item)" color="primary" data-cy="edit" outlined x-small>Edit</v-btn>
              </td>
              <td>
                <v-btn @click="showTagHistory(item)" data-cy="auditHistory" outlined x-small>Change History</v-btn>
              </td>
              <td>
                <v-btn
                  v-if="item.releaseNotesUsingTag === 0"
                  @click="showDeleteDialog(item)"
                  color="red"
                  data-cy="delete"
                  outlined
                  x-small
                  >Delete</v-btn
                >
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </v-col>
    <admin-tag-form v-model="showEdit" :tagList="tagList" mode="edit"></admin-tag-form>
    <admin-tag-form v-model="showCreate" :tagList="tagList" mode="create"></admin-tag-form>
    <admin-tag-change-history v-model="showTag"></admin-tag-change-history>
    <admin-tag-delete-confirmation v-model="showDelete"></admin-tag-delete-confirmation>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import AdminTagChangeHistory from "./AdminTagChangeHistory.vue";
import AdminTagDeleteConfirmation from "./AdminTagDeleteConfirmation.vue";
import AdminTagForm from "./AdminTagForm";
import GenericLoader from "@/components/GenericLoader";
import ReleaseNoteTagUsageDialog from "./ReleaseNoteTagUsageDialog.vue";

import tagApi from "@/api/tag";

export default {
  components: {
    AdminTagChangeHistory,
    AdminTagDeleteConfirmation,
    AdminTagForm,
    GenericLoader,
    ReleaseNoteTagUsageDialog,
  },
  async created() {
    try {
      await this.fetchTagList(this.serviceAreaId);
    } catch (error) {
      console.error(e);
      this.$notifyError({ message: "Error fetching organization tags." });
    }
  },
  computed: {
    ...mapState({
      tagList: state => state.admin.tag.list,
      isLoadingTags: state => state.admin.tag.isLoadingTags,
      serviceAreaId: state => state.session.serviceArea,
    }),
  },
  data() {
    return {
      headers: [
        {
          text: "Tag",
          align: "left",
          sortable: true,
          value: "label",
        },
        {
          text: "Description",
          align: "left",
          value: "description",
        },
        {
          text: "Usage",
        },
        {
          sortable: false,
          text: "Edit",
        },
        {
          sortable: false,
          text: "Change History",
        },
        {
          sortable: false,
          text: "",
        },
      ],
      releaseNoteList: [],
      search: null,
      showCreate: false,
      showDelete: false,
      showEdit: false,
      showTag: false,
      showUsage: false,
      sortBy: "id",
      sortDesc: false,
      tagAuditHistory: [],
    };
  },
  methods: {
    showCreateForm() {
      this.setEdit({});
      this.showCreate = true;
    },
    showDeleteDialog(tag) {
      this.setTag(Object.assign({}, tag));
      this.showDelete = true;
    },
    showEditForm(tag) {
      this.setEdit(Object.assign({}, tag));
      this.showEdit = true;
    },
    async showTagHistory(tag) {
      this.setTag(Object.assign({}, tag));
      try {
        await this.fetchTagHistory(tag.id);
        this.showTag = true;
      } catch (e) {
        console.error(e);
        this.$notifyError({ message: "Error fetching tag history." });
      }
    },
    async showReleaseNoteUsageModal(tag) {
      try {
        const releaseNoteList = await tagApi.getReleaseNoteListByTagId(tag.id, this.serviceAreaId);
        this.releaseNoteList = releaseNoteList;
        this.showUsage = true;
      } catch (e) {
        console.error(e);
        this.$notifyError({ message: "Error fetching release notes using tag." });
      }
    },
    ...mapActions({
      fetchTagList: "admin/tag/fetchList",
      fetchTagHistory: "auditLog/fetchTag",
    }),
    ...mapMutations({
      setEdit: "admin/tag/setEdit",
      setTag: "admin/tag/setTag",
    }),
  },
};
</script>
